import {
  type ComponentPropsWithoutRef,
  type KeyboardEvent,
  type PointerEvent,
  forwardRef
} from 'react'

import { LABELS } from '@/constants/common/message.constants'

import { COLUMN_WIDTH_MAX, COLUMN_WIDTH_MIN } from '../../-models/column-layout'

import { type HeaderCellResizeProps } from './apply-column-layout'

/** 矢印キー 1 回で変える幅（px）。Shift を押していればこの 5 倍 */
const KEYBOARD_RESIZE_STEP = 10

type Props = ComponentPropsWithoutRef<'th'> & Partial<HeaderCellResizeProps>

/**
 * 右端のつまみをドラッグして列幅を変えられる見出しセル（#3690 VOC_PJ-577）。
 * antd Table は列幅のドラッグ変更を標準で持たないため、`components.header.cell` を差し替えて作る。
 *
 * - ドラッグ中は onResizeWidth で幅を伝え続け（画面の追従用）、離したときに onResizeEnd で確定する
 *   （保存はそこで 1 回だけ行う）
 * - つまみの pointerdown / click は見出しへ伝えない。伝えると antd のソートのクリックが反応する
 */
export const ResizableHeaderCell = forwardRef<HTMLTableCellElement, Props>(
  function ResizableHeaderCell(
    { width, onResizeWidth, onResizeEnd, children, style, ...rest },
    ref
  ) {
    if (width === undefined || !onResizeWidth || !onResizeEnd) {
      return (
        <th ref={ref} {...rest} style={style}>
          {children}
        </th>
      )
    }

    const handlePointerDown = (event: PointerEvent<HTMLSpanElement>) => {
      event.preventDefault()
      event.stopPropagation()
      const handle = event.currentTarget
      const startX = event.clientX
      let latest = width
      handle.setPointerCapture(event.pointerId)

      const onMove = (moveEvent: globalThis.PointerEvent) => {
        latest = width + moveEvent.clientX - startX
        onResizeWidth(latest)
      }
      const onUp = () => {
        handle.removeEventListener('pointermove', onMove)
        handle.removeEventListener('pointerup', onUp)
        handle.removeEventListener('pointercancel', onUp)
        onResizeEnd(latest)
      }
      handle.addEventListener('pointermove', onMove)
      handle.addEventListener('pointerup', onUp)
      handle.addEventListener('pointercancel', onUp)
    }

    // キーボードでも幅を変えられるようにする（フォーカスして左右の矢印キー）。
    // 見出しのソートは Enter などのキーにも反応するので、つまみで受けたキーは見出しへ伝えない
    const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
      const direction =
        event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
      event.stopPropagation()
      if (direction === 0) return
      event.preventDefault()
      const step = KEYBOARD_RESIZE_STEP * (event.shiftKey ? 5 : 1)
      onResizeEnd(width + direction * step)
    }

    return (
      // 固定列の見出しは rc-table が position: sticky を付ける。sticky のままでもつまみ（absolute）の
      // 基準になるので、position を指定していないセルにだけ relative を足す
      <th ref={ref} {...rest} style={{ position: 'relative', ...style }}>
        {children}
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={LABELS.RESIZE_COLUMN}
          aria-valuenow={width}
          aria-valuemin={COLUMN_WIDTH_MIN}
          aria-valuemax={COLUMN_WIDTH_MAX}
          tabIndex={0}
          className="absolute right-0 top-0 z-[1] h-full w-2 cursor-col-resize select-none hover:bg-primary/30 focus-visible:bg-primary/30 focus-visible:outline-none"
          onPointerDown={handlePointerDown}
          onKeyDown={handleKeyDown}
          onClick={(event) => event.stopPropagation()}
        />
      </th>
    )
  }
)
